import { Suspense, FC, useState, useRef } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { FormattedMessage } from 'dibs-react-intl';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel';
import VisibilityTracker from 'dibs-visibility-tracker/exports/VisibilityTracker';
import { SbRespInteriorsPhotoGallery } from './SbRespInteriorsPhotoGallery';
import { SbRespInteriorsPhotoCarousel } from './SbRespInteriorsPhotoCarousel';
import { SbSharedInteriorsArticles } from '../SbSharedInteriorsModule/SbSharedInteriorsArticles';
import { SbSharedInteriorsPhotoModalRendererLazy } from '../SbSharedInteriorsModule/SbSharedInteriorsPhotoModal/SbSharedInteriorsPhotoModalRendererLazy';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';
import {
    trackModuleViewed,
    trackPhotoClicked,
    trackViewInteriorCTAClicked,
    trackModuleScroll,
    trackPhotoSave,
} from '../SbSharedInteriorsModule/interiorsModuleHelpers';

import styles from './SbRespInteriorsModule.scss';

import { SbRespInteriorsModule_editorial } from './__generated__/SbRespInteriorsModule_editorial.graphql';
import { SbRespInteriorsModule_itemSearch } from './__generated__/SbRespInteriorsModule_itemSearch.graphql';
import { SbRespInteriorsModule_user } from './__generated__/SbRespInteriorsModule_user.graphql';

type Props = {
    editorial: SbRespInteriorsModule_editorial | null;
    itemSearch: SbRespInteriorsModule_itemSearch;
    user: SbRespInteriorsModule_user;
    isClient: boolean;
};

const SbRespInteriorsModuleComponent: FC<Props> = ({ editorial, itemSearch, user, isClient }) => {
    const hasArticles = (editorial?.totalResults || 0) > 0;
    const interiorPhotos = itemSearch.interiorPhotos;
    const photos = (interiorPhotos?.photos || []).filter(filterNulls);
    const meta = itemSearch.meta;
    const displayHeader = meta?.attributeDisplayName || meta?.header;
    const showMainHeader = hasArticles && displayHeader;

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [photoIndex, setPhotoIndex] = useState(0);
    const [isModuleVisible, setIsModuleVisible] = useState(false);

    const elementRef = useRef(null);

    const handleVisibilityChange = ({ isVisible }: { isVisible: boolean }): void => {
        if (isVisible) {
            setIsModuleVisible(true);
            trackModuleViewed({ interiorPhotos });
        }
    };

    const handlePhotoClick = (): void => {
        setIsModalOpen(true);
        trackPhotoClicked({ interiorPhotos });
    };

    const handleViewInteriorClick = (): void => {
        setIsModalOpen(true);
        trackViewInteriorCTAClicked({ interiorPhotos });
    };

    const onPhotoLikeAdded = (): void => {
        trackPhotoSave({ interiorPhotos });
    };

    return (
        <>
            {showMainHeader && (
                <HeadingLevel>
                    {Heading => (
                        <Heading className={styles.header}>
                            <FormattedMessage
                                id="abf.SbRespInteriorsModule.header"
                                defaultMessage="More {name} Inspiration"
                                values={{
                                    name: displayHeader,
                                }}
                            />
                        </Heading>
                    )}
                </HeadingLevel>
            )}
            <VisibilityTracker
                onVisibilityChange={handleVisibilityChange}
                elementRef={elementRef}
            />
            <div className={styles.wrapper} ref={elementRef}>
                <div className={styles.content}>
                    {hasArticles ? (
                        <>
                            <SbRespInteriorsPhotoGallery
                                interiorPhotos={interiorPhotos}
                                itemSearch={itemSearch}
                                onPhotoClick={handlePhotoClick}
                                photoIndex={photoIndex}
                                onPhotoIndexChange={nextIndex => {
                                    setPhotoIndex(nextIndex);
                                    trackModuleScroll({ interiorPhotos });
                                }}
                                isClient={isClient}
                                onPhotoLikeAdded={onPhotoLikeAdded}
                                user={user}
                                isModuleVisible={isModuleVisible}
                            />
                            <SbSharedInteriorsArticles imgSizes="192px" editorial={editorial} />
                        </>
                    ) : (
                        <SbRespInteriorsPhotoCarousel
                            interiorPhotos={interiorPhotos}
                            photos={photos}
                            itemSearch={itemSearch}
                            onViewInteriorClick={handleViewInteriorClick}
                            onPhotoClick={handlePhotoClick}
                            onPhotoIndexChange={setPhotoIndex}
                            isClient={isClient}
                            onPhotoLikeAdded={onPhotoLikeAdded}
                            user={user}
                            isModuleVisible={isModuleVisible}
                        />
                    )}
                </div>
            </div>
            {isModalOpen && (
                <Suspense fallback="">
                    <SbSharedInteriorsPhotoModalRendererLazy
                        photoIndex={photoIndex}
                        interiorPhotos={interiorPhotos}
                        onClose={() => setIsModalOpen(false)}
                        user={user}
                    />
                </Suspense>
            )}
        </>
    );
};

export const SbRespInteriorsModule = createFragmentContainer(SbRespInteriorsModuleComponent, {
    itemSearch: graphql`
        fragment SbRespInteriorsModule_itemSearch on ItemSearchQueryConnection {
            ...SbRespInteriorsPhotoCarousel_itemSearch
            ...SbRespInteriorsPhotoGallery_itemSearch
            meta {
                attributeDisplayName
                header
            }
            interiorPhotos {
                ...SbSharedInteriorsPhotoModalRenderer_interiorPhotos
                ...SbRespInteriorsPhotoGallery_interiorPhotos
                ...SbRespInteriorsPhotoCarousel_interiorPhotos
                ...interiorsModuleHelpers_interiorPhotos
                photos {
                    ...SbRespInteriorsPhotoCarousel_photos
                }
            }
        }
    `,
    editorial: graphql`
        fragment SbRespInteriorsModule_editorial on WordpressArticlesType {
            ...SbSharedInteriorsArticles_editorial
            totalResults
        }
    `,
    user: graphql`
        fragment SbRespInteriorsModule_user on User {
            ...SbSharedInteriorsPhotoModalRenderer_user
            ...SbRespInteriorsPhotoGallery_user
            ...SbRespInteriorsPhotoCarousel_user
        }
    `,
});
